/**
 * Football 101 in the match HUD (renders `ui.tutorial`, written by the lesson engine): the title slate, the coach
 * panel (bottom-right, in the scorebug's slot), the world layer (bubble over your head, field guides, the tell),
 * HUD spotlights and progressive reveal, the lesson-clear / graduation cards, the broadcast-moment gate and the
 * coach's voice. docs/TUTORIAL.md §3.6.
 */
import './tutorial.css';
import { effect } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  Difficulty,
  randomSeed,
  SpecialsMode,
  specialInfo,
  characterById,
  type CharacterId,
} from '@hf/shared';
import { useActions, useServices } from '../actions';
import { Glove, Head, Key, SpecialIcon } from '../components';
import { buildOfflineSetup, draft, pendingMatch } from '@hf/client-core/ui-model/flow';
import { inputStyle, LAYER, useArming, useFocusGrid, useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import type { MatchSetup, TutorialView } from '@hf/client-core/ui-model/types';
import { CardArt, MiniSquare } from './cards';
import { faceBox } from '@hf/client-core/ui-model/tutorial/head-dodge';
import { LESSONS, lessonTitleFor, type HudPart } from '@hf/client-core/ui-model/tutorial/catalog';
import { doneCount, progress } from '@hf/client-core/ui-model/tutorial/progress';
import { KeyChip, RichLine, useTutorialDevice, Cap } from './keys';
import { Spotlights } from './Spotlights';
import { WorldLayer } from './WorldLayer';
import { playCoach } from '@hf/client-core/ui-model/tutorial/coach-voice';
import { openHowToPlay } from './HowToPlay';
import { tutorialOpponent } from './start';

const LESSON_COUNT = LESSONS.length;

const ALL_PARTS: HudPart[] = [
  'meters',
  'stamina',
  'power',
  'playclock',
  'rush',
  'chyron',
  'scorebug',
  'captions',
  'moments',
];

export function TutorialOverlay({ setup }: { setup: MatchSetup | null }) {
  const t = ui.tutorial.value;
  useMomentGate();
  useVoice(t);
  if (!t || !t.phase) return null;
  const hud = new Set(t.hud ?? ALL_PARTS);
  // the cards say it all: no announcer captions over them
  if (t.phase !== 'beat') hud.delete('captions');
  const hidden = ALL_PARTS.filter((p) => !hud.has(p)).join(' ');
  const spots = (t.spotlight ?? []).map((s) => s.part).join(' ');
  return (
    <div
      class={`hf-tutorial phase-${t.phase} ${t.demo ? 'demo' : ''}`}
      data-hide={hidden}
      data-spot={spots}
      data-play={t.live ? 'live' : 'dead'}
      data-clock={t.clockHeld ? 'held' : 'run'}
      data-testid="tutorial"
    >
      {t.phase === 'beat' || t.phase === 'slate' ? <WorldLayer view={t} /> : null}
      {t.phase === 'beat' ? <Spotlights view={t} /> : null}
      {t.phase === 'slate' ? <Slate view={t} /> : null}
      {t.phase === 'beat' && t.beatKind === 'card' && t.card && t.card.kind !== 'none' ? (
        <div class="hf-cardstage" key={`${t.lessonId}:${t.beatIndex}`}>
          <CardArt kind={t.card.kind} head={(t.head ?? 'rookie') as CharacterId} />
        </div>
      ) : null}
      {/* the tracker steps aside while the full square is the card (they overlapped) */}
      {t.phase === 'beat' && t.square && t.card?.kind !== 'square' ? <MiniSquare cells={t.square} /> : null}
      {/* Game Day keeps the coach up during plays too (score + drive in its tab): it used to collapse into a
          scoreboard, so a first-timer had no guidance mid-drive */}
      {t.phase === 'beat' ? <CoachPanel view={t} /> : null}
      {t.phase === 'clear' ? <ClearCard view={t} setup={setup} /> : null}
      {t.phase === 'complete' ? <Graduation view={t} setup={setup} /> : null}
    </div>
  );
}

// ── title slate ──────────────────────────────────────────────────────────────────────────────────────────────

function Slate({ view }: { view: TutorialView }) {
  const head = (view.head ?? 'rookie') as CharacterId;
  const title = lessonTitleFor(view.lessonId ?? 'move', head);
  return (
    <div class="hf-slate" role="status" aria-live="assertive" key={view.lessonId}>
      <div class="hf-slate__band">
        <div class="hf-slate__num">
          <small class="t-broadcast">LESSON</small>
          <b class="t-display">{view.lessonNumber}</b>
        </div>
        <div class="hf-slate__text">
          <div class="hf-slate__kicker t-broadcast">FOOTBALL 101 · {campName(view)}</div>
          <div class="hf-slate__title t-display">{title}</div>
          <div class="hf-slate__goal">{view.goal}</div>
        </div>
        <div class="hf-slate__art" aria-hidden="true">
          <Head character={head} expression="determined" class="hf-slate__head" />
          <Glove side="home" pose="ready" class="hf-slate__glove" />
        </div>
      </div>
    </div>
  );
}

const campName = (v: TutorialView): string =>
  (v.lessonNumber ?? 1) <= 5 ? 'ROOKIE CAMP' : (v.lessonNumber ?? 1) <= 11 ? 'PRO CAMP' : 'GAME DAY';

// ── the coach panel ──────────────────────────────────────────────────────────────────────────────────────────

function CoachPanel({ view }: { view: TutorialView }) {
  const device = useTutorialDevice();
  const actions = useActions();
  const flash = view.flash;
  // the ✓ belongs to the line it came with ("TACKLED!", "Nice! One more."): it never lingers onto the next
  // instruction (QA round 3: "✓ All those yards lead HERE…" read as already done)
  const flashLine = view.done ?? view.hint ?? view.say ?? '';
  const [pop, setPop] = useState<{ kind: string; id: number; line: string } | null>(null);
  // the panel remounts per lesson: a flash it MOUNTED with belongs to the previous lesson (or the beat before the
  // slate) and never pops on this lesson's first instruction (QA round 4: "✓ Every play starts with a SNAP")
  const mountFlash = useRef(flash?.id ?? -1);
  useEffect(() => {
    if (!flash || flash.id === mountFlash.current) return;
    setPop({ ...flash, line: flashLine });
    const t = setTimeout(() => setPop((p) => (p?.id === flash.id ? null : p)), 900);
    return () => clearTimeout(t);
  }, [flash?.id]);
  const head = (view.head ?? 'rookie') as CharacterId;
  const card = view.beatKind === 'card';
  const scrimmage = view.lessonId === 'scrimmage';
  // The panel never fades (it used to, over a landing ring, and became unreadable exactly when it mattered):
  // beats whose ball comes down bottom-right dock it top-right instead, and turf guides that still end up behind
  // it lift their labels above it (WorldLayer).
  const line = view.done ?? view.hint ?? view.say ?? '';
  // a diagnosis after a failed try is amber with "!"; an extra instruction (nothing failed) keeps the whistle; a
  // lesson learned that is no win ("REFUSED") is blue with an "i", never a green ✓
  const tone = view.done
    ? view.doneTone === 'info'
      ? 'learned'
      : 'done'
    : view.hint
      ? view.hintKind === 'nudge'
        ? 'nudge'
        : 'hint'
      : view.demo
        ? 'demo'
        : '';
  // the last try's diagnosis under this try's live play: dimmed, so it never reads as about THIS try
  const stale = tone === 'hint' && !!view.hintStale;
  const count = view.count ?? 1;
  const panel = useRef<HTMLDivElement>(null);
  useMetersClear(panel);
  useHeadsClear(panel);
  const kb = inputStyle.value === 'kb';
  const pad = inputStyle.value === 'pad';
  const showKeys = !card && !view.done && (view.keys?.length ?? 0) > 0;
  return (
    <div
      class={`hf-coach dock-${view.dock ?? 'bottom'} ${tone} ${stale ? 'stale' : ''} ${pop ? `pop-${pop.kind}` : ''} ${card ? 'card' : ''} ${view.idle ? 'idle' : ''}`}
      ref={panel}
      role="region"
      aria-label="Coach"
      aria-live="polite"
      key={`${view.lessonId}`}
    >
      <div class="hf-coach__top">
        <div class="hf-coach__tab">
          <span class="hf-coach__lesson t-broadcast">
            {view.lessonNumber} · {lessonTitleFor(view.lessonId ?? 'move', head)}
          </span>
          {scrimmage && view.scrimmage ? (
            <span class="hf-coach__game t-broadcast">
              <b class="home">{view.scrimmage.score[0]}</b>–<b class="away">{view.scrimmage.score[1]}</b>
              <span class="hf-coach__sep" aria-hidden="true">
                ·
              </span>
              <small>{view.scrimmage.drive === 'yours' ? 'YOUR DRIVE 1/1' : 'THEIR DRIVE 1/1'}</small>
            </span>
          ) : (
            <span
              class="hf-coach__beats"
              aria-label={`Step ${(view.beatIndex ?? 0) + 1} of ${view.beatCount}`}
            >
              {Array.from({ length: view.beatCount ?? 1 }, (_, i) => (
                <i key={i} class={i < (view.beatIndex ?? 0) ? 'done' : i === view.beatIndex ? 'now' : ''} />
              ))}
            </span>
          )}
          {view.demo ? (
            <span class="hf-coach__watch t-broadcast">
              <i aria-hidden="true" /> WATCH
            </span>
          ) : null}
          {(view.slowmo ?? 1) < 0.99 ? <span class="hf-coach__slow t-broadcast">SLOW-MO</span> : null}
        </div>
        <div class="hf-coach__aux t-broadcast">
          {view.offerSkip ? (
            <>
              <button class="hf-coach__skip offer interactive" onClick={() => actions.tutorialSkip()}>
                {kb ? <Key label="N" /> : pad ? <Key label="START" pad /> : null} SKIP THIS ONE
              </button>
              <span class="hf-coach__sep">·</span>
              <span>OR KEEP TRYING</span>
            </>
          ) : (
            <>
              {scrimmage ? null : kb ? (
                <span>
                  <Key label="R" /> RETRY
                </span>
              ) : pad ? (
                <span>
                  <Key label="VIEW" pad /> RETRY
                </span>
              ) : null}
              {/* a pad has no SKIP button: SKIP lives in its START menu (a bare "SKIP" with no glyph read as a
                  broken prompt — QA round 2) */}
              {pad ? null : (
                <button class="hf-coach__skip interactive" onClick={() => actions.tutorialSkip()}>
                  {kb ? <Key label="N" /> : null} {scrimmage ? 'END GAME' : 'SKIP'}
                </button>
              )}
              {kb ? (
                <span>
                  <Key label="ESC" /> MENU
                </span>
              ) : pad ? (
                <span>
                  <Key label="START" pad /> MENU · {scrimmage ? 'END GAME' : 'SKIP'}
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div class="hf-coach__body">
        <div class={`hf-coach__whistle ${tone}`} aria-hidden="true">
          {tone === 'done' ? '✓' : tone === 'hint' ? '!' : tone === 'learned' ? 'i' : <WhistleIcon />}
          {/* the ✓ stamp lands ON the panel's status icon (it sat on the tab, over the lesson number, and a second
              ✓ showed in the icon at the same time) */}
          {pop?.kind === 'success' && pop.line === line ? (
            <div class="hf-coach__stamp t-display" key={pop.id}>
              ✓
            </div>
          ) : null}
        </div>
        <div class="hf-coach__main">
          <p class={`hf-coach__line ${tone}`} key={line}>
            <RichLine text={line} device={device} />
          </p>
          {count > 1 && !card ? (
            <div class="hf-coach__reps" aria-label={`${view.reps} of ${count}`}>
              {Array.from({ length: count }, (_, i) => (
                <i key={i} class={i < (view.reps ?? 0) ? 'on' : ''} />
              ))}
              <small class="t-broadcast">
                {view.reps} / {count}
              </small>
            </div>
          ) : null}
        </div>
        {showKeys ? (
          <div class="hf-coach__keys">
            {view.keys!.map((k, i) => (
              <KeyChip k={k} device={device} key={i} />
            ))}
          </div>
        ) : null}
        {card ? (
          <button
            class="hf-coach__continue interactive t-broadcast"
            onClick={() => ui.tutorialCommand({ kind: 'card' })}
          >
            <Cap button="A" device={device} /> CONTINUE
          </button>
        ) : view.holding ? (
          <button
            class="hf-coach__continue hold interactive t-broadcast"
            onClick={() => ui.tutorialCommand({ kind: 'skip' })}
          >
            <Cap button="A" device={device} /> NEXT
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A coach panel docked at the top clears the HUD meters above it (QA round 3: its tab and RETRY · SKIP · MENU strip
 * sat on P2's POWER bar and special disc): `--dock-clear` = the bottom of every meter column the panel's width
 * overlaps, plus a gap. Measured (the meters' height depends on the scale, the touch layout and MUST BE REAL),
 * a few times a second.
 */
const useMetersClear = (panel: { current: HTMLDivElement | null }): void => {
  useEffect(() => {
    let raf = 0;
    let n = 0;
    let last = '';
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      if (n++ % 6 !== 0) return;
      const el = panel.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const u = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
      let clear = 0;
      const under = (r: DOMRect, right = r.right): void => {
        if (r.width < 2 || r.height < 2) return;
        if (right <= box.left || r.left >= box.right) return;
        clear = Math.max(clear, r.bottom + Math.max(6, 14 * u));
      };
      for (const m of document.querySelectorAll('.hf-hud .hf-meters')) under(m.getBoundingClientRect());
      // the clock row (pause · play clock), and the rush count that docks to its right at every snap: counted
      // from the start, so the panel never jumps at the snap
      const row = document.querySelector('.hf-hud .hf-hud__clockrow');
      if (row) {
        const r = row.getBoundingClientRect();
        under(r, r.right + r.width * 1.6);
      }
      const v = clear > 0 ? `${Math.round(clear)}px` : '';
      if (v === last) return;
      last = v;
      if (v) el.style.setProperty('--dock-clear', v);
      else el.style.removeProperty('--dock-clear');
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
};

/**
 * The panel's lesson tab and its RETRY · SKIP · MENU strip stick up above its body, into the field: when a head
 * stands under them they fade right down (QA round 4: the strip sat on Big Tom's beard and chin whenever he stood
 * right of centre). The body itself sits under the heads' feet line (its text never fades).
 */
const useHeadsClear = (panel: { current: HTMLDivElement | null }): void => {
  useEffect(() => {
    let raf = 0;
    let n = 0;
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      if (n++ % 3 !== 0) return;
      const el = panel.current;
      if (!el) return;
      const t = ui.teach;
      const faces = t.active ? [t.head, t.opp].filter((h) => h.on && h.r > 0).map((h) => faceBox(h)) : [];
      for (const [sel, cls] of [
        ['.hf-coach__aux', 'occ-aux'],
        ['.hf-coach__tab', 'occ-tab'],
      ] as const) {
        const part = el.querySelector(sel)?.getBoundingClientRect();
        const hit =
          !!part &&
          part.width > 0 &&
          faces.some((f) => part.right > f.l && part.left < f.r && part.bottom > f.t && part.top < f.b);
        if (el.classList.contains(cls) !== hit) el.classList.toggle(cls, hit);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
};

function WhistleIcon() {
  return (
    <svg viewBox="0 0 48 48" class="hf-whistle">
      <path d="M8 24a12 12 0 1 0 24 0h8v-8H20A12 12 0 0 0 8 24z" fill="currentColor" />
      <circle cx="20" cy="24" r="4.5" fill="var(--ink)" />
      <rect x="34" y="12" width="6" height="4" rx="1" fill="currentColor" />
    </svg>
  );
}

// ── lesson clear / graduation ────────────────────────────────────────────────────────────────────────────────

/** How long a clear / graduation card ignores the keyboard and pad after it appears. */
export const CARD_ARM_MS = 800;

/**
 * The lesson-clear and Game Day cards appear while the player is still PLAYING (J snaps, D runs, J dives): their
 * grids take no device press for CARD_ARM_MS after mount, and a press meanwhile keeps them shut until the keys have
 * been quiet a moment (useArming). QA round 4: the snap J after "TURNOVER ON DOWNS! …" launched an Exhibition from
 * the graduation card before anyone saw it, and a D held from the last run moved lesson 2's card to REPLAY.
 * Directions are swallowed too (the focus grid's own arming only blocks confirm / back / pause). Clicks always work.
 */
const useCardArming = (): boolean => {
  const arming = useArming(CARD_ARM_MS);
  useMenuLayer(
    (_a, source) => {
      if (source.kind !== 'ui') arming.poke();
    },
    !arming.armed,
    LAYER.HUD + 1,
  );
  return arming.armed;
};

/** Start an Exhibition vs the Easy CPU with the head the player learned with (via the Versus splash). */
const startEasyGame = (head: CharacterId, goto: (s: 'versus') => void): void => {
  const opp = tutorialOpponent(head);
  const setup = buildOfflineSetup(
    {
      ...draft.peek(),
      mode: 'cpu',
      difficulty: Difficulty.EASY,
      characters: [head, opp],
      specials: SpecialsMode.SIGNATURE,
    },
    randomSeed(),
    ui.settings.peek().playerName,
  );
  pendingMatch.value = {
    setup,
    venue: 'HEAD BOWL STADIUM · EXHIBITION',
    tags: ['P1 · FOOTBALL 101 GRAD', 'CPU · EASY'],
  };
  goto('versus');
};

function ClearCard({ view }: { view: TutorialView; setup: MatchSetup | null }) {
  const actions = useActions();
  const { audio } = useServices();
  const device = useTutorialDevice();
  const head = (view.head ?? 'rookie') as CharacterId;
  const camp = view.campClear === 'rookie';
  // Rookie Camp never teaches defense, and half of every game is defense (QA round 1: the first real game after it
  // went offside twice and lost 0–25): its card leads to lesson 6 (DEFENSE, a minute) and offers the game second.
  // The DEFENSE card is where both halves are known: it offers the real game.
  const defense = view.lessonId === 'defense';
  const game = {
    id: 'game',
    label: 'PLAY A GAME',
    sub: 'VS THE EASY CPU',
    run: () => startEasyGame(head, actions.goto),
  };
  const items = camp
    ? [
        {
          id: 'next',
          label: view.next ? `NEXT: ${lessonTitleFor(view.next.id, head)} ▶` : 'KEEP LEARNING ▶',
          sub: view.next ? `LESSON ${view.next.number} · 1 MIN` : 'PRO CAMP',
          run: () => ui.tutorialCommand({ kind: 'next' }),
        },
        game,
        { id: 'lessons', label: 'LESSONS', sub: '', run: () => actions.goto('tutorial') },
      ]
    : [
        {
          id: 'next',
          label: view.next ? 'NEXT ▶' : 'FINISH',
          sub: view.next ? `${view.next.number} · ${lessonTitleFor(view.next.id, head)}` : '',
          run: () => (view.next ? ui.tutorialCommand({ kind: 'next' }) : actions.goto('tutorial')),
        },
        ...(defense ? [game] : []),
        { id: 'replay', label: 'REPLAY', sub: '', run: () => ui.tutorialCommand({ kind: 'replay' }) },
        { id: 'lessons', label: 'LESSONS', sub: '', run: () => actions.goto('tutorial') },
      ];
  const armed = useCardArming();
  const grid = useFocusGrid({
    count: items.length,
    horizontal: true,
    priority: LAYER.HUD,
    active: ui.overlay.value === 'none',
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => {
      playSfx(audio, UI_SFX.confirm);
      items[i]?.run();
    },
  });
  return (
    <div class={`hf-clear ${armed ? 'armed' : ''}`} role="dialog" aria-label="Lesson clear">
      <div class="hf-clear__scrim" />
      <div class={`hf-clear__card ${camp ? 'camp' : ''}`}>
        <div class="hf-clear__stamp t-display">{camp ? 'ROOKIE CAMP COMPLETE!' : 'LESSON CLEAR!'}</div>
        <div class="hf-clear__row">
          <div class="hf-clear__art" aria-hidden="true">
            <Head character={head} expression="celebrating" class="hf-clear__head" />
            <Glove side="home" pose="fist" class="hf-clear__glove" />
          </div>
          <div class="hf-clear__text">
            <div class="hf-clear__kicker t-broadcast">
              LESSON {view.lessonNumber} · {lessonTitleFor(view.lessonId ?? 'move', head)}
            </div>
            <p class="hf-clear__goal">{view.goal}</p>
            {camp ? (
              <p class="hf-clear__sub">
                The goal: <b>score touchdowns, stop his.</b> You know the snap, downs, the end zone and the
                pass. Next: <b>DEFENSE</b>, half of every game. One minute, then you are ready for a real one.
              </p>
            ) : defense ? (
              <p class="hf-clear__sub">
                Score touchdowns, stop his: you can play a real game now. Or keep learning:{' '}
                <b>the counter square</b> beats every tackle.
              </p>
            ) : view.next ? (
              <p class="hf-clear__sub">
                Up next: <b>{lessonTitleFor(view.next.id, head)}</b>
              </p>
            ) : null}
          </div>
        </div>
        <div class="hf-clear__btns">
          {items.map((it, i) => (
            <button
              key={it.id}
              // the gold face IS the focus: it moves with it (a gold NEXT beside a focused REPLAY read as two
              // highlighted buttons — QA round 4)
              class={`hf-btn ${i === grid.index ? '' : 'dark'} hf-clear__btn interactive`}
              {...grid.item(i)}
            >
              <span>{it.label}</span>
              {it.sub ? <small class="t-broadcast">{it.sub}</small> : null}
            </button>
          ))}
        </div>
        <div class="hf-clear__hint t-broadcast">
          <Cap button="A" device={device} size="sm" /> {items[grid.index]?.label}
        </div>
      </div>
    </div>
  );
}

/**
 * The end of Game Day. Finishing it finishes Football 101 (12/12 ✓: playtest #4 found "only a win counts" a wall a
 * newcomer could not pass); the result is honest: a win is "YOU MADE THE TEAM!" (+ the ★ WON badge), a loss is a
 * good first game with a REMATCH first in line, and the CPU eases off a notch after each loss.
 */
function Graduation({ view }: { view: TutorialView; setup: MatchSetup | null }) {
  const actions = useActions();
  const { audio } = useServices();
  const head = (view.head ?? 'rookie') as CharacterId;
  const sc = view.scrimmage;
  const result = sc?.result ?? 'win';
  const lost = result === 'loss';
  // only a win makes the team (progress records a loss or a tie as PLAYED): anything else offers the rematch first
  const again = result !== 'win';
  const rematch = { id: 'rematch', label: 'REMATCH', run: () => ui.tutorialCommand({ kind: 'replay' }) };
  const items = [
    ...(again ? [rematch] : []),
    { id: 'game', label: 'PLAY A GAME', run: () => startEasyGame(head, actions.goto) },
    ...(again ? [] : [{ id: 'howto', label: 'HOW TO PLAY', run: () => openHowToPlay() }]),
    { id: 'menu', label: 'MAIN MENU', run: () => actions.quitMatch() },
  ];
  const armed = useCardArming();
  const grid = useFocusGrid({
    count: items.length,
    horizontal: true,
    priority: LAYER.HUD,
    active: ui.overlay.value === 'none',
    onMove: () => playSfx(audio, UI_SFX.move),
    onConfirm: (i) => {
      playSfx(audio, UI_SFX.confirm);
      items[i]?.run();
    },
  });
  const sp = specialInfo(characterById(head).signature);
  const done = doneCount(progress.value);
  return (
    <div class={`hf-clear grad ${armed ? 'armed' : ''}`} role="dialog" aria-label="Football 101 complete">
      <div class="hf-clear__scrim" />
      <div class={`hf-clear__card grad ${result}`}>
        <div class="hf-clear__stamp t-display">
          {result === 'win' ? 'YOU MADE THE TEAM!' : result === 'tie' ? 'ALL SQUARE!' : 'GOOD FIRST GAME!'}
        </div>
        <div class="hf-clear__row">
          <div class="hf-clear__art" aria-hidden="true">
            <Head character={head} expression={lost ? 'determined' : 'celebrating'} class="hf-clear__head" />
            <Glove side="home" pose={lost ? 'ready' : 'fist'} class="hf-clear__glove" />
          </div>
          <div class="hf-clear__text">
            <div class="hf-clear__kicker t-broadcast">
              {done >= LESSON_COUNT
                ? `FOOTBALL 101 COMPLETE · ${LESSON_COUNT} / ${LESSON_COUNT} ✓`
                : `GAME DAY ✓ · ${done} / ${LESSON_COUNT} LESSONS DONE`}
            </div>
            {sc ? (
              <div class="hf-grad__score t-broadcast">
                <span class="home">{sc.score[0]}</span>
                <small>{result === 'win' ? 'YOU WIN' : result === 'tie' ? 'TIED' : 'FINAL'}</small>
                <span class="away">{sc.score[1]}</span>
              </div>
            ) : null}
            {sc ? (
              <div class="hf-grad__stats">
                <span>
                  <b class="t-display">{sc.yards}</b>
                  <small class="t-broadcast">YARDS</small>
                </span>
                <span>
                  <b class="t-display">{sc.tackles}</b>
                  <small class="t-broadcast">TACKLES</small>
                </span>
                <span>
                  <b class="t-display">{sc.baits}</b>
                  <small class="t-broadcast">FAKES LANDED</small>
                </span>
              </div>
            ) : null}
            <p class="hf-clear__sub">
              {lost ? (
                <>
                  You know every rule now. Bonus: win a REMATCH for the <b>★ WON</b> badge (he goes a little
                  easier each time), or try a full game. Your special is <b>{sp.name}</b>.
                </>
              ) : result === 'tie' ? (
                <>
                  Dead even! Win a REMATCH for the <b>★ WON</b> badge, or try a full game. Your special is{' '}
                  <b>{sp.name}</b>.
                </>
              ) : (
                <>
                  <SpecialIcon special={characterById(head).signature} class="hf-grad__icon" /> You beat the
                  CPU. Next stop: a real game. Your special is <b>{sp.name}</b>.
                </>
              )}
            </p>
          </div>
        </div>
        <div class="hf-clear__btns">
          {items.map((it, i) => (
            <button
              key={it.id}
              // the gold face IS the focus: it moves with it (a gold NEXT beside a focused REPLAY read as two
              // highlighted buttons — QA round 4)
              class={`hf-btn ${i === grid.index ? '' : 'dark'} hf-clear__btn interactive`}
              {...grid.item(i)}
            >
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── broadcast moments & voice ────────────────────────────────────────────────────────────────────────────────

/**
 * Lessons mute the broadcast graphics except the ones the beat allows (`view.moments`, a regex source): no
 * "GAIN OF 1" lower-thirds over the coach, no stack of four messages. The scrimmage allows everything.
 */
const useMomentGate = (): void => {
  useEffect(
    () =>
      effect(() => {
        const t = ui.tutorial.value;
        if (!t || !t.phase) return;
        const allow = t.moments ?? '';
        const title = ui.superTitle.value;
        if (title && (allow === '' || !new RegExp(allow, 'i').test(title.text))) ui.superTitle.value = null;
        // lower thirds ("GAIN OF 1") belong to the real game: only the scrimmage shows them — minus the match
        // underneath's "DRIVE 1 OF 10" (Game Day is one drive each; the Game Day bug says so)
        const low = ui.lowerThird.value;
        if (low && (t.lessonId !== 'scrimmage' || /DRIVE \d+ OF/i.test(`${low.title} ${low.sub ?? ''}`)))
          ui.lowerThird.value = null;
      }),
    [],
  );
};

/** The coach's voice (announcer lines; the bridge captions them) and the ✓ / ✗ sounds. */
const useVoice = (t: TutorialView | null): void => {
  const { audio } = useServices();
  const lastVoice = useRef(-1);
  const lastFlash = useRef(-1);
  useEffect(() => {
    const v = t?.voice;
    if (!v || v.id === lastVoice.current) return;
    lastVoice.current = v.id;
    try {
      playCoach(audio, v);
    } catch {
      /* audio optional */
    }
  }, [t?.voice?.id]);
  useEffect(() => {
    const f = t?.flash;
    if (!f || f.id === lastFlash.current) return;
    lastFlash.current = f.id;
    try {
      if (f.kind === 'success') {
        audio.sfx('ui_ready', { volume: 0.9 });
        audio.crowdReact?.('cheer', 0.35);
      } else if (f.kind === 'info')
        // a lesson learned, not a win ("REFUSED"): a neutral tick, never the error buzzer
        audio.sfx('ui_tick', { volume: 0.7 });
      else audio.sfx('ui_error', { volume: 0.6 });
    } catch {
      /* audio optional */
    }
  }, [t?.flash?.id]);
};
